
import { CalendarClock, FileCheck, GitCompare, PieChart, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../Button";

const ContractLifecycleContent = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Contract Lifecycle Management</h1>
        <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
          AI-powered Contract Lifecycle Management (CLM) systems provide legal teams with advanced tools to streamline every stage of contract handling.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-20">
        <div className="prose prose-lg prose-invert max-w-4xl mx-auto">
          <p>
            AI-powered Contract Lifecycle Management (CLM) systems provide legal teams with advanced tools to streamline every stage of contract handling, from creation to post-signature management. Benefits include faster contract turnaround times, improved accuracy, reduced risks, enhanced productivity, and centralized contract management. These systems empower legal teams to focus on strategic tasks while maintaining compliance and operational efficiency.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-blue-400">
              <FileCheck size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Automated Drafting and Review</h3>
            <p className="text-white/70">
              AI generates contract drafts using predefined templates and clauses, reducing manual effort. It also accelerates review processes by identifying risks, inconsistencies, and missing terms.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-purple-400">
              <GitCompare size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Risk Mitigation and Compliance</h3>
            <p className="text-white/70">
              AI ensures contracts adhere to regulatory standards by detecting non-compliant language and potential risks, minimizing errors and legal disputes.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-green-400">
              <CalendarClock size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Workflow Automation</h3>
            <p className="text-white/70">
              Routine tasks like approval routing, obligation tracking, and milestone reminders are automated, enhancing efficiency and reducing delays.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-amber-400">
              <PieChart size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Contract Analytics</h3>
            <p className="text-white/70">
              AI analyzes contract data to provide actionable insights, uncover trends, and predict potential breaches, aiding strategic decision-making.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-red-400">
              <Users size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Collaboration Tools</h3>
            <p className="text-white/70">
              Features like real-time collaboration and version control improve communication among stakeholders and ensure consistent contract updates.
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Curated Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Legal Team Efficiency</h3>
            <p className="text-white/70 mb-4">
              How a One-Woman Legal Team Reduced Contracting Time to Close by 80%+ in Four Months
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://ironcladapp.com/customers/demostack-legal-ops/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Contract Automation</h3>
            <p className="text-white/70 mb-4">
              How Glassdoor Automated 2,000+ Contracts
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://ironcladapp.com/customers/glassdoor/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Template Consolidation</h3>
            <p className="text-white/70 mb-4">
              How Orangetheory Consolidated a Thousand Contract Templates in 3 Months with AI Assist
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://ironcladapp.com/customers/orangetheory/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Corporate Legal Innovation</h3>
            <p className="text-white/70 mb-4">
              The Future of Legal Work: How Harvey is Driving The Adecco Group Forward
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.harvey.ai/customers/the-adecco-group', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Knowledge Search</h3>
            <p className="text-white/70 mb-4">
              Homburger Elevates Client Service and Efficiency with AI-Powered Knowledge Search
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.deepjudge.ai/customers/success-story-homburger-elevates-client-service-and-efficiency-with-ai-powered-knowledge-search', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">PI Firm Savings</h3>
            <p className="text-white/70 mb-4">
              How One PI firm Saves Big Using Filevine and Arctrieval
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.filevine.com/customers/case-studies/how-one-pi-firm-saves-big-using-filevine-and-arctrieval/', '_blank')}
            >
              Read Study
            </Button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Contract Management?</h2>
        <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
          Contact our team today to learn how our AI-powered CLM solutions can streamline your legal operations.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact">
            <Button variant="glass" size="lg" hasAnimation={true}>Contact Us</Button>
          </Link>
          <Link to="/services/custom-solutions">
            <Button variant="outline" size="lg">Explore Custom Solutions</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ContractLifecycleContent;
